// Browser-only persistence of the user's vault claim. Enforces the
// 1:1 user→vault rule client-side: the next claim attempt for the same
// wallet short-circuits to this stored record. Server-side enforcement
// (and a logged-in lookup endpoint) lands when GAR is integrated.

const KEY = 'cef-vault:vault';

export interface StoredVault {
  walletAddress: string;
  vaultId: string;
  vaultName: string;
  /**
   * Whether the vault's write-delegation grant — the on-chain NonTransfer
   * proxy to vault-api, submitted by `vault.ensure()` during the claim — is
   * known to be registered. Persisted so it OUTLIVES the claim that produced
   * it.
   *
   * Three states, and the third is the point:
   *   `true`      — the claim ran to completion (the SDK's grant step throws
   *                 out of `ensure` on failure), or a re-authorize confirmed
   *                 it.
   *   `false`     — a LEGACY record from the old gateway-grant track (which
   *                 never succeeded for anyone). Kept as "unconfirmed", never
   *                 rewritten by guesswork.
   *   `undefined` — unknown (record from a build predating this field, or a
   *                 vault claimed on another device). Must not be reported as
   *                 granted, and must not raise an alarm either.
   *
   * Why persisted at all: this used to live only in React state, so a reload
   * erased it and the Settings control fell back to deriving "granted?" from
   * `vault.status`. That is the CREDENTIAL's status — 'active' whenever
   * vault-api minted a working credential during `ensure` — and it is
   * independent of whether the proxy grant that lets it RENEW is known to
   * this device.
   *
   * Not authoritative, and deliberately so: the SDK exposes no way to ASK the
   * chain whether the proxy is still in place from this surface, so this is
   * the best available signal, biased to fail safe — absence never reads as
   * "granted".
   */
  delegateGranted?: boolean;
}

function isStoredVault(v: unknown): v is StoredVault {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  // `delegateGranted` is optional, but a present-and-wrong-typed value is
  // corruption, not a legacy record — reject rather than silently coerce it,
  // because coercing `'yes'` to truthy is exactly the false "enabled" this
  // field exists to prevent.
  if (o.delegateGranted !== undefined && typeof o.delegateGranted !== 'boolean') return false;
  return typeof o.walletAddress === 'string'
    && typeof o.vaultId === 'string'
    && typeof o.vaultName === 'string';
}

export function getStoredVault(walletAddress?: string): StoredVault | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredVault(parsed)) return null;
    if (walletAddress && parsed.walletAddress !== walletAddress) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredVault(v: StoredVault): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(KEY, JSON.stringify(v));
}

export function clearStoredVault(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(KEY);
}
